import { ReactNode } from 'react';

import classNames from 'classnames';

import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@ui/text/text/text';

import { getPublishedStatus, selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import { useAppSelector } from '@/store/hooks';

import useChildHeight from '../hooks/useChildHeight';

import styles from './settings-edit-panel.module.css';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

interface ISettingsEditPanelProps {
  children: ReactNode;
  isEditing: boolean;
  onCancel: () => void;
  onSave: () => void;
  onPreview?: () => void;
}

const SettingsEditPanel = ({ children, isEditing, onCancel, onSave, onPreview }: ISettingsEditPanelProps) => {
  const isSaving = useAppSelector(selectIsSavingWorkingProject);
  const publishedStatus = useAppSelector(getPublishedStatus);

  const editContainerClasses = classNames(
    'flex-column',
    'gap-16',
    'jc-flex-end',
    'position-absolute',
    styles['settings-edit-container']
  );

  const [childrenWrapperRef, childrenHeight] = useChildHeight(children);

  return (
    <div className={styles['settings-widget']}>
      <Container
        className={styles['settings-detail-container']}
        margin={isEditing ? '20px' : '0'}
        height={isEditing ? `${childrenHeight + 60}px` : `${childrenHeight}px`}
      >
        <div ref={childrenWrapperRef}>{children}</div>
      </Container>
      <Container
        className={editContainerClasses}
        background="var(--gray-900)"
        padding="20px"
        width="100%"
        height={isEditing ? `${childrenHeight + 90}px` : `${childrenHeight}px`}
        opacity={isEditing ? '1' : '0'}
        borderRadius="12px"
      >
        <div
          inert={!isEditing}
          className="flex ai-center jc-space-between"
          style={{
            ...(isEditing && { zIndex: '10' }),
          }}
        >
          <OptionalComponent display={!!onPreview}>
            <Button
              backgroundColor="var(--gray-700)"
              hoverBackgroundColor="var(--gray-600)"
              height="32px"
              className="L2 padding-v-7 padding-h-24"
              onClick={onPreview}
              disabled={!publishedStatus?.url}
              disabledOpacity={0.6}
            >
              Preview
            </Button>
          </OptionalComponent>
          <div className="flex ai-center gap-8 flex-1 jc-flex-end">
            <Button
              backgroundColor="var(--gray-700)"
              hoverBackgroundColor="var(--gray-600)"
              height="32px"
              className="L2 padding-v-7 padding-h-24"
              onClick={onCancel}
            >
              <Text className="L2">Cancel</Text>
            </Button>
            <Button
              backgroundColor={getThemeColorCSSVariable()}
              padding="8px 24px"
              width="auto"
              onClick={onSave}
              disabled={isSaving}
              disabledOpacity={0.6}
              hoverOpacity={0.8}
            >
              <Text
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                className="L2"
              >
                Save
              </Text>
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default SettingsEditPanel;
